import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Fuel, 
  Gauge, 
  Calendar, 
  Settings2, 
  Palette, 
  DoorOpen,
  Send,
  ChevronLeft,
  ChevronRight,
  Heart,
  Calculator,
  Bell,
  BellRing,
  BadgeCheck,
  Scale,
  CarFront,
  Phone
} from 'lucide-react';
import { toast } from 'sonner';
import { Navbar, Footer } from '@/components/Layout';
import { carsApi, inquiriesApi, priceAlertApi, settingsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { useFavorites } from '@/context/FavoritesContext';
import { useLanguage } from '@/context/LanguageContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CarDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { settings: siteSettings } = useSiteSettings();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [inquiryForm, setInquiryForm] = useState({
    customer_name: '',
    customer_email: '',
    message: '',
    subscribe_newsletter: false
  });
  const [sending, setSending] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();
  
  // Financing state
  const [financingSettings, setFinancingSettings] = useState(null);
  const [downPayment, setDownPayment] = useState(0);
  const [selectedMonths, setSelectedMonths] = useState(36);
  
  // Price alert state
  const [showPriceAlert, setShowPriceAlert] = useState(false);
  const [priceAlertForm, setPriceAlertForm] = useState({ email: '', name: '', target_price: 0 });
  
  // Compare state
  const [isInComparison, setIsInComparison] = useState(false);

  // Get sold banner colors from settings
  const soldBannerGradient = siteSettings ? 
    `linear-gradient(to right, ${siteSettings.sold_banner_color_from || '#059669'}, ${siteSettings.sold_banner_color_via || '#22c55e'}, ${siteSettings.sold_banner_color_to || '#059669'})` :
    'linear-gradient(to right, #059669, #22c55e, #059669)';
  
  const soldBannerText = siteSettings ? 
    (language === 'en' ? (siteSettings.sold_banner_text_en || 'Sold') : (siteSettings.sold_banner_text || 'Verkauft')) :
    'Verkauft';

  // Scroll to top when page loads or car ID changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Track page view
  useEffect(() => {
    const trackView = async () => {
      try {
        let sessionId = localStorage.getItem('analytics_session');
        if (!sessionId) {
          sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem('analytics_session', sessionId);
        }
        
        await fetch(`${API_URL}/api/analytics/track?page=car_detail&car_id=${id}&session_id=${sessionId}`, {
          method: 'POST'
        });
      } catch (error) {
        // Silently fail - analytics shouldn't break the page
      }
    };
    
    if (id) {
      trackView();
    }
  }, [id]);

  const [savingAlert, setSavingAlert] = useState(false);

  const isCarFavorite = car ? isFavorite(car.id) : false;

  // Check if car is in comparison
  useEffect(() => {
    const checkComparison = async () => {
      try {
        let sessionId = localStorage.getItem('comparison_session_id');
        if (!sessionId) {
          sessionId = 'cmp_' + Math.random().toString(36).substr(2, 9);
          localStorage.setItem('comparison_session_id', sessionId);
        }
        
        const response = await fetch(`${API_URL}/api/compare/${sessionId}`);
        const data = await response.json();
        if (car && data.cars) {
          setIsInComparison(data.cars.some(c => c.id === car.id));
        }
      } catch (error) {
        // Silently fail
      }
    };
    if (car) {
      checkComparison();
    }
  }, [car]);

  const handleCompareToggle = async () => {
    if (!car) return;
    
    let sessionId = localStorage.getItem('comparison_session_id');
    if (!sessionId) {
      sessionId = 'cmp_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('comparison_session_id', sessionId);
    }
    
    try {
      if (isInComparison) {
        // Remove from comparison
        await fetch(`${API_URL}/api/compare/${sessionId}/${car.id}`, { method: 'DELETE' });
        setIsInComparison(false);
        toast.success('Vom Vergleich entfernt');
      } else {
        // Get current comparison
        const response = await fetch(`${API_URL}/api/compare/${sessionId}`);
        const data = await response.json();
        const currentCars = data.cars || [];
        
        if (currentCars.length >= 4) {
          toast.error('Maximal 4 Fahrzeuge im Vergleich möglich');
          return;
        }
        
        // Add to comparison
        const carIds = [...currentCars.map(c => c.id), car.id];
        await fetch(`${API_URL}/api/compare`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ car_ids: carIds, session_id: sessionId })
        });
        setIsInComparison(true);
        toast.success('Zum Vergleich hinzugefügt');
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Vergleichs');
    }
  };

  // Get description based on language
  const getDescription = () => {
    if (!car) return '';
    if (language === 'en' && car.description_en) {
      return car.description_en;
    }
    return car.description;
  };

  const handleFavoriteClick = async () => {
    if (!car) return;
    const success = await toggleFavorite(car.id);
    if (success) {
      toast.success(isCarFavorite ? 'Von Merkliste entfernt' : 'Zur Merkliste hinzugefügt');
    }
  };

  useEffect(() => {
    loadCar();
    loadFinancingSettings();
  }, [id]);

  useEffect(() => {
    if (car && financingSettings) {
      const defaultPercent = financingSettings.financing_default_down_payment_percent || 20;
      setDownPayment(Math.round(car.price * (defaultPercent / 100)));
      setPriceAlertForm(prev => ({ ...prev, target_price: Math.round(car.price * 0.9) }));
    }
  }, [car, financingSettings]);

  const loadFinancingSettings = async () => {
    try {
      const response = await settingsApi.get();
      setFinancingSettings(response.data);
    } catch (error) {
      console.error('Error loading financing settings:', error);
    }
  };

  const loadCar = async () => {
    try {
      const response = await carsApi.getById(id);
      setCar(response.data);
    } catch (error) {
      console.error('Error loading car:', error);
      toast.error('Car not found');
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (url) => {
    if (!url) return 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800';
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await inquiriesApi.create({
        ...inquiryForm,
        car_id: id
      });
      toast.success('Anfrage erfolgreich gesendet! Wir melden uns bald bei Ihnen.');
      setInquiryForm({ customer_name: '', customer_email: '', message: '', subscribe_newsletter: false });
    } catch (error) {
      toast.error('Fehler beim Senden. Bitte versuchen Sie es erneut.');
    } finally {
      setSending(false);
    }
  };

  const handlePriceAlertSubmit = async (e) => {
    e.preventDefault();
    if (priceAlertForm.target_price >= car.price) {
      toast.error('Zielpreis muss unter dem aktuellen Preis liegen');
      return;
    }
    setSavingAlert(true);
    try {
      await priceAlertApi.create(id, priceAlertForm.email, priceAlertForm.target_price, priceAlertForm.name || null);
      toast.success('Preisalarm erstellt! Wir benachrichtigen Sie bei Preissenkung.');
      setShowPriceAlert(false);
    } catch (error) {
      toast.error('Fehler beim Erstellen des Preisalarms');
    } finally {
      setSavingAlert(false);
    }
  };

  // Calculate monthly payment
  const calculateMonthlyPayment = () => {
    if (!car || !financingSettings) return 0;
    const principal = car.price - downPayment;
    if (principal <= 0) return 0;
    
    const monthlyRate = (financingSettings.financing_interest_rate || 4.9) / 100 / 12;
    const n = selectedMonths;
    
    if (monthlyRate === 0) return principal / n;
    
    const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
    return Math.round(payment);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const nextImage = () => {
    if (car?.images?.length > 1) {
      setActiveImage((prev) => (prev + 1) % car.images.length);
    }
  };

  const prevImage = () => {
    if (car?.images?.length > 1) {
      setActiveImage((prev) => (prev - 1 + car.images.length) % car.images.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-32 pb-24">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12">
            <div className="animate-pulse">
              <div className="aspect-[16/9] bg-muted mb-8" />
              <div className="h-8 bg-muted w-1/4 mb-4" />
              <div className="h-12 bg-muted w-1/2 mb-8" />
              <div className="h-4 bg-muted w-3/4" />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!car) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-32 pb-24 text-center">
          <h1 className="font-heading text-4xl mb-4">{t('car_not_found')}</h1>
          <Link to="/cars" className="btn-ghost">{t('back_to_inventory')}</Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Format mileage
  const formatMileage = (km) => {
    if (!km) return null;
    return `${km.toLocaleString('de-DE')} km`;
  };

  // Format first registration
  const formatFirstRegistration = () => {
    if (!car.first_registration_month && !car.first_registration_year) return null;
    if (car.first_registration_month && car.first_registration_year) {
      const monthNames = language === 'en' 
        ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        : ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
      return `${monthNames[car.first_registration_month - 1]} ${car.first_registration_year}`;
    }
    return car.first_registration_year?.toString();
  };

  // Vehicle type labels
  const getVehicleTypeLabel = () => {
    if (!car.vehicle_type) return null;
    const types = {
      'new': language === 'en' ? 'New' : 'Neuwagen',
      'used': language === 'en' ? 'Used' : 'Gebraucht',
      'demo': language === 'en' ? 'Demo' : 'Vorführwagen'
    };
    return types[car.vehicle_type] || car.vehicle_type;
  };

  // Build specs array with conditional items
  const specs = [
    { icon: Calendar, label: t('detail_year'), value: car.year },
    ...(car.mileage ? [{ icon: Gauge, label: language === 'en' ? 'Mileage' : 'Kilometerstand', value: formatMileage(car.mileage) }] : []),
    { icon: Gauge, label: t('detail_horsepower'), value: `${car.horsepower} ${t('hp')}` },
    { icon: Fuel, label: t('detail_fuel'), value: car.fuel_type },
    { icon: Settings2, label: t('detail_transmission'), value: car.transmission },
    { icon: Palette, label: t('detail_color'), value: car.color },
    { icon: DoorOpen, label: t('detail_doors'), value: car.doors }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* SEO Meta Tags - only render when car data is available */}
      {car && (
        <Helmet>
          <title>{`${car.year} ${car.brand} ${car.model} | Tim Automobile`}</title>
          <meta name="description" content={`${car.year} ${car.brand} ${car.model} - ${car.horsepower} PS, ${(car.mileage || 0).toLocaleString('de-DE')} km, ${car.fuel_type}, ${car.transmission}. Preis: € ${(car.price || 0).toLocaleString('de-DE')}. Jetzt anfragen bei Tim Automobile.`} />
          <meta name="keywords" content={`${car.brand}, ${car.model}, ${car.year}, Gebrauchtwagen, Premium, Luxusauto, ${car.fuel_type}, kaufen`} />
          
          {/* Open Graph / Facebook */}
          <meta property="og:type" content="product" />
          <meta property="og:title" content={`${car.year} ${car.brand} ${car.model}`} />
          <meta property="og:description" content={`${car.horsepower} PS • ${(car.mileage || 0).toLocaleString('de-DE')} km • € ${(car.price || 0).toLocaleString('de-DE')}`} />
          <meta property="og:url" content={`${API_URL}/cars/${car.id}`} />
          {car.images?.[0] && <meta property="og:image" content={getImageUrl(car.images[0])} />}
          
          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${car.year} ${car.brand} ${car.model}`} />
          <meta name="twitter:description" content={`${car.horsepower} PS • ${(car.mileage || 0).toLocaleString('de-DE')} km • € ${(car.price || 0).toLocaleString('de-DE')}`} />
          {car.images?.[0] && <meta name="twitter:image" content={getImageUrl(car.images[0])} />}
          
          {/* Structured Data */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Vehicle",
              "name": `${car.year} ${car.brand} ${car.model}`,
              "brand": { "@type": "Brand", "name": car.brand },
              "model": car.model,
              "vehicleModelDate": String(car.year),
              "mileageFromOdometer": { "@type": "QuantitativeValue", "value": car.mileage || 0, "unitCode": "KMT" },
              "fuelType": car.fuel_type,
              "vehicleTransmission": car.transmission,
              "color": car.color,
              "offers": {
                "@type": "Offer",
                "price": car.price || 0,
                "priceCurrency": "EUR",
                "availability": "https://schema.org/InStock"
              },
              "image": car.images?.map(img => getImageUrl(img)) || []
            })}
          </script>
        </Helmet>
      )}
      
      <Navbar />

      {/* Back Link */}
      <div className="pt-24 md:pt-28">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <Link 
            to="/cars" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft size={16} />
            <span className="text-sm uppercase tracking-wider">{t('back_to_inventory')}</span>
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-8 md:py-12" data-testid="car-detail">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Image Gallery - 2 columns */}
            <div className="lg:col-span-2">
              {/* Main Image */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="relative aspect-[16/10] bg-card border border-border overflow-hidden group"
              >
                <img
                  src={getImageUrl(car.images?.[activeImage])}
                  alt={`${car.brand} ${car.model}`}
                  className={`w-full h-full object-cover ${car.is_sold ? 'grayscale-[30%]' : ''}`}
                />
                
                {/* SOLD Banner Overlay */}
                {car.is_sold && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="absolute inset-0 bg-black/10" />
                    <div className="relative transform -rotate-12">
                      <div 
                        className="px-12 py-4 shadow-2xl"
                        style={{ background: soldBannerGradient }}
                      >
                        <div className="flex items-center gap-3">
                          <BadgeCheck size={28} className="text-white" />
                          <span className="text-white font-bold text-2xl tracking-widest uppercase">
                            {soldBannerText}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Navigation Arrows */}
                {car.images?.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-card/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid="prev-image"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-card/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid="next-image"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </>
                )}
              </motion.div>

              {/* Thumbnails */}
              {car.images?.length > 1 && (
                <div className="flex gap-3 mt-4 overflow-x-auto hide-scrollbar">
                  {car.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveImage(index)}
                      className={`gallery-thumbnail flex-shrink-0 w-24 h-16 ${
                        activeImage === index ? 'active' : ''
                      }`}
                      data-testid={`thumbnail-${index}`}
                    >
                      <img
                        src={getImageUrl(img)}
                        alt={`${car.brand} ${car.model} - ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}

              {/* Description */}
              <div className="mt-10">
                <h2 className="font-heading text-2xl mb-4">{t('detail_about')}</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {getDescription()}
                </p>
              </div>
            </div>

            {/* Details & Inquiry - 1 column */}
            <div className="lg:col-span-1">
              {/* Car Info */}
              <div className="bg-card border border-border p-6 md:p-8">
                {/* Sold Badge */}
                {car.is_sold && (
                  <div className="mb-4 p-3 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <BadgeCheck className="text-emerald-600" size={20} />
                      <span className="text-sm font-semibold text-emerald-800 uppercase tracking-wide">
                        Dieses Fahrzeug wurde verkauft
                      </span>
                    </div>
                  </div>
                )}
                
                <div className="flex justify-between items-start mb-2">
                  <p className="overline">{car.brand}</p>
                  {!car.is_sold && (
                    <button
                      onClick={handleFavoriteClick}
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                        isCarFavorite 
                          ? 'bg-primary text-white' 
                          : 'bg-muted text-muted-foreground hover:bg-primary hover:text-white'
                      }`}
                      data-testid="detail-favorite-btn"
                      aria-label={isCarFavorite ? 'Remove from wishlist' : 'Add to wishlist'}
                    >
                      <Heart size={18} fill={isCarFavorite ? 'currentColor' : 'none'} />
                    </button>
                  )}
                </div>
                <h1 className="font-heading text-3xl md:text-4xl font-light mb-2">
                  {car.model}
                </h1>
                <p className={`font-heading text-3xl md:text-4xl mb-8 ${car.is_sold ? 'text-emerald-600' : 'text-primary'}`}>
                  {formatPrice(car.price)}
                  {car.is_sold && <span className="text-sm font-medium ml-2 uppercase">Verkauft</span>}
                </p>

                {/* Vehicle Type Badge */}
                {car.vehicle_type && (
                  <div className="mb-4">
                    <span className={`inline-block px-3 py-1 text-xs font-medium uppercase tracking-wider rounded-full ${
                      car.vehicle_type === 'new' ? 'bg-green-100 text-green-700' :
                      car.vehicle_type === 'demo' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {getVehicleTypeLabel()}
                    </span>
                  </div>
                )}

                {/* Specs Grid */}
                <div className="grid grid-cols-2 gap-4">
                  {specs.map(({ icon: Icon, label, value }) => (
                    <div key={label} className="border-b border-border pb-3">
                      <div className="flex items-center gap-2 text-muted-foreground mb-1">
                        <Icon size={14} strokeWidth={1.5} />
                        <span className="text-xs uppercase tracking-wider">{label}</span>
                      </div>
                      <p className="text-sm font-medium">{value}</p>
                    </div>
                  ))}
                </div>

                {/* Additional Details */}
                {(formatFirstRegistration() || car.previous_owners !== null || car.location || car.internal_id) && (
                  <div className="mt-6 pt-4 border-t border-border space-y-3">
                    {formatFirstRegistration() && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{language === 'en' ? 'First Registration' : 'Erstzulassung'}</span>
                        <span className="font-medium">{formatFirstRegistration()}</span>
                      </div>
                    )}
                    {car.previous_owners !== null && car.previous_owners !== undefined && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{language === 'en' ? 'Previous Owners' : 'Vorbesitzer'}</span>
                        <span className="font-medium">{car.previous_owners}</span>
                      </div>
                    )}
                    {car.location && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{language === 'en' ? 'Location' : 'Standort'}</span>
                        <span className="font-medium">{car.location}</span>
                      </div>
                    )}
                    {car.internal_id && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{language === 'en' ? 'Reference' : 'Referenz'}</span>
                        <span className="font-medium text-muted-foreground">{car.internal_id}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Test Drive Button - Directly after car info */}
              {siteSettings?.module_test_drive_enabled && !car.is_sold && (
                <div className="bg-card border border-border p-6 md:p-8 mt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <CarFront size={24} className="text-primary" />
                    <div>
                      <h3 className="font-heading text-xl">{language === 'de' ? 'Probefahrt buchen' : 'Book Test Drive'}</h3>
                      <p className="text-sm text-muted-foreground">{language === 'de' ? 'Erleben Sie dieses Fahrzeug hautnah' : 'Experience this vehicle firsthand'}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/test-drive?car=${car.id}`)}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white hover:bg-primary/90 rounded-lg transition-colors font-medium"
                    data-testid="test-drive-btn"
                  >
                    <Calendar size={18} />
                    {language === 'de' ? 'Jetzt Termin vereinbaren' : 'Schedule Appointment'}
                  </button>
                </div>
              )}

              {/* Inquiry Form - Only show if not sold */}
              {!car.is_sold ? (
                <>
                  {/* Direct Phone Contact Box */}
                  {siteSettings?.contact_phone && (
                    <div className="bg-gradient-to-br from-gold/10 via-gold/5 to-transparent border border-gold/40 p-5 md:p-6 mt-6 rounded-lg">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-gold text-white flex items-center justify-center flex-shrink-0">
                          <Phone size={22} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                            {language === 'de' ? 'Lieber telefonisch?' : 'Prefer to call?'}
                          </p>
                          <p className="text-sm text-muted-foreground mb-2">
                            {language === 'de'
                              ? 'Kontaktieren Sie uns direkt per Telefon:'
                              : 'Contact us directly by phone:'}
                          </p>
                          <a
                            href={`tel:${(siteSettings.contact_phone || '').replace(/\s+/g, '')}`}
                            className="inline-flex items-center gap-2 text-xl md:text-2xl font-heading text-gold hover:text-gold-dark hover:underline transition-colors"
                            data-testid="car-detail-phone-link"
                          >
                            {siteSettings.contact_phone}
                          </a>
                          {siteSettings.contact_hours && (
                            <p className="text-xs text-muted-foreground mt-2">
                              {siteSettings.contact_hours}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                <div className="bg-card border border-border p-6 md:p-8 mt-6">
                  <h3 className="font-heading text-xl mb-4">{t('inquiry_title')}</h3>
                  <p className="text-muted-foreground text-sm mb-6">
                    {t('inquiry_subtitle')}
                  </p>

                  <form onSubmit={handleInquirySubmit} className="space-y-4">
                    <div>
                      <input
                        type="text"
                        value={inquiryForm.customer_name}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, customer_name: e.target.value })}
                        className="input-premium w-full"
                        placeholder={t('detail_your_name')}
                        required
                        data-testid="inquiry-name"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        value={inquiryForm.customer_email}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, customer_email: e.target.value })}
                        className="input-premium w-full"
                        placeholder={t('detail_your_email')}
                        required
                        data-testid="inquiry-email"
                      />
                    </div>
                    <div>
                      <textarea
                        value={inquiryForm.message}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                        className="input-premium w-full h-24 resize-none"
                        placeholder={t('detail_your_message')}
                        required
                        data-testid="inquiry-message"
                      />
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={inquiryForm.subscribe_newsletter}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, subscribe_newsletter: e.target.checked })}
                        className="w-4 h-4 mt-0.5 accent-gold"
                        data-testid="inquiry-newsletter"
                      />
                      <span className="text-sm text-muted-foreground">
                        {t('inquiry_newsletter')}
                      </span>
                    </label>
                    <Button
                      type="submit"
                      className="btn-primary w-full flex items-center justify-center gap-2"
                      disabled={sending}
                      data-testid="inquiry-submit"
                    >
                      {sending ? (
                        <div className="spinner" />
                      ) : (
                        <>
                          {t('detail_send_inquiry')}
                          <Send size={16} />
                        </>
                      )}
                    </Button>
                  </form>
                </div>
                </>
              ) : (
                <div className="bg-card border border-border p-6 md:p-8 mt-6">
                  <div className="text-center py-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center">
                      <BadgeCheck className="text-emerald-600" size={32} />
                    </div>
                    <h3 className="font-heading text-xl mb-2">Bereits verkauft</h3>
                    <p className="text-muted-foreground text-sm mb-4">
                      Dieses Fahrzeug ist nicht mehr verfügbar. Schauen Sie sich unsere anderen Premium-Fahrzeuge an.
                    </p>
                    <Link 
                      to="/cars" 
                      className="btn-primary inline-flex items-center gap-2"
                    >
                      Weitere Fahrzeuge entdecken
                    </Link>
                  </div>
                </div>
              )}

              {/* Financing Calculator */}
              {financingSettings && financingSettings.financing_enabled !== false && !car.is_sold && (
                <div className="bg-card border border-border p-6 md:p-8 mt-6">
                  <h3 className="font-heading text-xl mb-4 flex items-center gap-2">
                    <Calculator size={20} className="text-primary" />
                    {language === 'en' 
                      ? (financingSettings.financing_title_en || 'Financing')
                      : (financingSettings.financing_title || 'Finanzierung')
                    }
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-muted-foreground block mb-2">
                        {language === 'en' 
                          ? (financingSettings.financing_down_payment_label_en || 'Down Payment')
                          : (financingSettings.financing_down_payment_label || 'Anzahlung')
                        }: <strong>{formatPrice(downPayment)}</strong>
                      </label>
                      <input
                        type="range"
                        min="0"
                        max={car.price}
                        step="500"
                        value={downPayment}
                        onChange={(e) => setDownPayment(parseInt(e.target.value))}
                        className="w-full accent-gold"
                        data-testid="financing-down-payment"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>€ 0</span>
                        <span>{formatPrice(car.price)}</span>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm text-muted-foreground block mb-2">
                        {language === 'en' 
                          ? (financingSettings.financing_term_label_en || 'Term')
                          : (financingSettings.financing_term_label || 'Laufzeit')
                        }
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {(financingSettings.financing_months || [6, 12, 24, 36, 48, 60]).map((months) => (
                          <button
                            key={months}
                            type="button"
                            onClick={() => setSelectedMonths(months)}
                            className={`px-3 py-2 text-sm border transition-colors ${
                              selectedMonths === months
                                ? 'bg-primary text-white border-primary'
                                : 'border-border hover:border-primary'
                            }`}
                            data-testid={`financing-months-${months}`}
                          >
                            {months} {language === 'en' ? 'mo.' : 'Mon.'}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="p-4 bg-muted rounded-lg text-center">
                      <p className="text-sm text-muted-foreground mb-1">
                        {language === 'en' 
                          ? (financingSettings.financing_monthly_label_en || 'Monthly Payment')
                          : (financingSettings.financing_monthly_label || 'Monatliche Rate')
                        }
                      </p>
                      <p className="text-3xl font-heading text-primary" data-testid="monthly-payment">
                        {formatPrice(calculateMonthlyPayment())}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {language === 'en' 
                          ? `at ${financingSettings.financing_interest_rate || 4.9}% p.a. ${financingSettings.financing_interest_label_en || 'effective annual interest'}`
                          : `bei ${financingSettings.financing_interest_rate || 4.9}% p.a. ${financingSettings.financing_interest_label || 'effektiver Jahreszins'}`
                        }
                      </p>
                    </div>

                    <p className="text-xs text-muted-foreground text-center">
                      {language === 'en' 
                        ? (financingSettings.financing_partner_text_en || 'In cooperation with our partner bank Santander')
                        : (financingSettings.financing_partner_text || 'In Kooperation mit unserer Partnerbank Santander')
                      }
                    </p>
                  </div>
                </div>
              )}

              {/* Price Alert */}
              <div className="bg-card border border-border p-6 md:p-8 mt-6">
                {!showPriceAlert ? (
                  <button
                    onClick={() => setShowPriceAlert(true)}
                    className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-border hover:border-primary text-muted-foreground hover:text-primary transition-colors"
                    data-testid="price-alert-btn"
                  >
                    <Bell size={18} />
                    Preisalarm erstellen
                  </button>
                ) : (
                  <div>
                    <h3 className="font-heading text-xl mb-4 flex items-center gap-2">
                      <BellRing size={20} className="text-primary" />
                      Preisalarm
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Wir benachrichtigen Sie per E-Mail, wenn der Preis auf Ihren Wunschpreis sinkt.
                    </p>
                    <form onSubmit={handlePriceAlertSubmit} className="space-y-4">
                      <div>
                        <label className="text-sm text-muted-foreground block mb-1">Zielpreis</label>
                        <input
                          type="number"
                          value={priceAlertForm.target_price}
                          onChange={(e) => setPriceAlertForm({ ...priceAlertForm, target_price: parseInt(e.target.value) })}
                          className="input-premium w-full"
                          max={car.price - 1}
                          required
                          data-testid="price-alert-target"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Aktueller Preis: {formatPrice(car.price)}
                        </p>
                      </div>
                      <div>
                        <input
                          type="text"
                          value={priceAlertForm.name}
                          onChange={(e) => setPriceAlertForm({ ...priceAlertForm, name: e.target.value })}
                          className="input-premium w-full"
                          placeholder="Ihr Name (optional)"
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          value={priceAlertForm.email}
                          onChange={(e) => setPriceAlertForm({ ...priceAlertForm, email: e.target.value })}
                          className="input-premium w-full"
                          placeholder="Ihre E-Mail"
                          required
                          data-testid="price-alert-email"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          onClick={() => setShowPriceAlert(false)}
                          className="btn-secondary flex-1"
                        >
                          Abbrechen
                        </Button>
                        <Button
                          type="submit"
                          className="btn-primary flex-1 flex items-center justify-center gap-2"
                          disabled={savingAlert}
                          data-testid="price-alert-submit"
                        >
                          {savingAlert ? <div className="spinner" /> : <Bell size={16} />}
                          Alarm erstellen
                        </Button>
                      </div>
                    </form>
                  </div>
                )}
              </div>

              {/* Compare Button */}
              <div className="bg-card border border-border p-6 md:p-8 mt-6">
                <button
                  onClick={handleCompareToggle}
                  className={`w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed transition-colors ${
                    isInComparison 
                      ? 'border-primary bg-primary/10 text-primary' 
                      : 'border-border hover:border-primary text-muted-foreground hover:text-primary'
                  }`}
                  data-testid="compare-btn"
                >
                  <Scale size={18} />
                  {isInComparison ? 'Vom Vergleich entfernen' : 'Zum Vergleich hinzufügen'}
                </button>
                {isInComparison && (
                  <Link 
                    to="/compare" 
                    className="block text-center text-sm text-primary mt-3 hover:underline"
                  >
                    Zum Fahrzeugvergleich →
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
